/**
 * Validates if a URL is a valid booking URL for accommodation redirects.
 *
 * This function supports two URL formats:
 * 1. **Full URLs**: URLs with http:// or https:// scheme (e.g., https://booking.com/hotel)
 * 2. **Domain patterns**: User-entered domain strings without protocol (e.g., booking.com, www.hotels.com)
 *
 * @param url - The URL string to validate. Can be null, undefined, or a string.
 *
 * @returns `true` if the URL is valid in either format, `false` otherwise.
 */
export const isValidBookingUrl = (url: Maybe<string>): boolean => {
  // Type guard: ensure url is a string
  if (typeof url !== 'string') {
    return false;
  }

  // Normalize: remove leading/trailing whitespace
  const trimmedUrl: string = url.trim();

  // Empty string check
  if (trimmedUrl.length === 0) {
    return false;
  }

  // Add https:// prefix if URL doesn't start with http:// or https://
  const urlToTest = trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://') ? trimmedUrl : `https://${trimmedUrl}`;

  // Validate using URL constructor
  try {
    const urlObject = new URL(urlToTest);

    // Reject if hostname contains URL-encoded characters (e.g., %20 for spaces)
    // Only invalid characters in the hostname itself (like spaces) will result in % in hostname
    if (urlObject.hostname.includes('%')) {
      return false;
    }

    // Reject if hostname is empty
    if (!urlObject.hostname || urlObject.hostname.length === 0) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};
